/**
 * Reine Helfer für useIssuesFilters (ausgelagert für Hook-Size-Limit).
 */
import type { ProductWithIssues } from '@/lib/api/channel-readiness'

export type IssuesSort = 'impact_desc' | 'count_desc' | 'count_asc' | 'name_asc'

export function tally(
  products: ProductWithIssues[],
  key: 'issue_type' | 'severity',
): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const p of products)
    for (const i of p.issues) {
      const v = key === 'issue_type' ? i.issue_type : (i.severity ?? 'info')
      counts[v] = (counts[v] ?? 0) + 1
    }
  return counts
}

const COMPARATORS: Record<
  IssuesSort,
  (a: ProductWithIssues, b: ProductWithIssues) => number
> = {
  impact_desc: (a, b) =>
    (b.revenue_30d ?? 0) * Math.max(b.issue_count, 1) -
    (a.revenue_30d ?? 0) * Math.max(a.issue_count, 1),
  count_desc: (a, b) => b.issue_count - a.issue_count,
  count_asc: (a, b) => a.issue_count - b.issue_count,
  name_asc: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
}

export function sortProducts(
  products: ProductWithIssues[],
  sort: IssuesSort,
): ProductWithIssues[] {
  return [...products].sort(COMPARATORS[sort])
}

export function toggleValue(
  set: Set<string>,
  fn: (s: Set<string>) => void,
): (v: string) => void {
  return (v: string) => {
    const next = new Set(set)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    fn(next)
  }
}

/** Kanal-Scope: Issues (+issue_count) je Produkt auf einen Kanal reduzieren, leere Produkte droppen. */
export function reduceToChannel(
  products: ProductWithIssues[],
  channelId: string | null,
): ProductWithIssues[] {
  if (!channelId) return products
  return products
    .map((p) => {
      const issues = p.issues.filter((i) => i.channel_id === channelId)
      return { ...p, issues, issue_count: issues.length }
    })
    .filter((p) => p.issue_count > 0)
}
